import { getDefaultPathIndexes } from '@exodus/asset-lib';
import assert from 'minimalistic-assert';
import KeyIdentifier from '@exodus/key-identifier';
const MODULE_ID = 'seedBasedMessageSigner';
class SeedBasedMessageSigner {
    #assetsModule;
    #keychain;
    #assetSources;
    constructor({ assetsModule, keychain, assetSources }) {
        this.#assetsModule = assetsModule;
        this.#keychain = keychain;
        this.#assetSources = assetSources;
    }
    #getKeyId = async (opts) => {
        const { baseAssetName, message, walletAccount, purpose } = opts;
        assert(typeof baseAssetName === 'string', 'baseAssetName is required');
        assert(walletAccount && typeof walletAccount === 'object', 'walletAccount is required');
        assert(message && typeof message === 'object', 'message is required');
        const baseAsset = this.#assetsModule.getAsset(opts.baseAssetName);
        const compatibilityMode = walletAccount.compatibilityMode;
        const purposes = await this.#assetSources.getSupportedPurposes({
            assetName: baseAssetName,
            walletAccount: walletAccount.toString(),
        });
        const defaultPurpose = purposes[0];
        const defaultPathIndexes = getDefaultPathIndexes({
            asset: baseAsset,
            walletAccount,
            compatibilityMode,
        });
        const { chainIndex, addressIndex } = defaultPathIndexes;
        const keyId = baseAsset.api.getKeyIdentifier({
            purpose: purpose ?? defaultPurpose,
            accountIndex: walletAccount.index,
            addressIndex,
            chainIndex,
            compatibilityMode,
        });
        return new KeyIdentifier(keyId);
    };
    #getSigner = ({ keyId, seedId }) => {
        return {
            getPublicKey: async () => this.#keychain.getPublicKey({ seedId, keyId }),
            sign: async ({ data, signatureType, enc, tweak, extraEntropy, }) => this.#keychain.signBuffer({
                seedId,
                keyId,
                data,
                signatureType,
                enc,
                tweak,
                extraEntropy,
            }),
        };
    };
    signMessage = async (opts) => {
        const { baseAssetName, message, walletAccount } = opts;
        const baseAsset = this.#assetsModule.getAsset(baseAssetName);
        assert(baseAsset, `baseAsset not found`);
        assert(baseAsset.api.features.signMessageWithSigner, `asset ${baseAssetName} does not support message signing`);
        const keyId = await this.#getKeyId(opts);
        return baseAsset.api.signMessage({
            signer: this.#getSigner({ keyId, seedId: walletAccount.seedId }),
            message,
        });
    };
}
const createSeedBasedMessageSigner = (opts) => new SeedBasedMessageSigner(opts);
const seedBasedMessageSignerDefinition = {
    id: MODULE_ID,
    type: 'module',
    factory: createSeedBasedMessageSigner,
    dependencies: ['assetsModule', 'keychain', 'assetSources'],
};
export default seedBasedMessageSignerDefinition;
